import * as React from 'react';
import Modal from '../../misc/components/modal';
import { OverreadStatusIdEnum, User, StudyCoughSetting } from '../../misc/api/apiTypes';
import Loading from '../../misc/components/loading';
import Alert, { AlertCategoryEnum } from '../../misc/components/Alert';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { CoughAnalysisSet, StudyCoughEventType, WebPortalClient } from 'webportalclient';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../misc/api/WebPortalTokenClient';
import { CoughAnalysisSetTypeEnum } from '../../misc/api/CoughAnalysisSetTypes';
import { DateTimeAccuracy, FormatTime } from '../../misc/components/LocalizedDateTime';

export interface CreateAdjudicationAnalysisSetModalProps {
    analysisSet: CoughAnalysisSet;
    onClose: () => void;
}

declare const rootUrl;

export const CreateAdjudicationAnalysisSetModal = (props: CreateAdjudicationAnalysisSetModalProps) => {
    const [error, setError] = React.useState<string | undefined>();
    const [submitting, setSubmitting] = React.useState(false);
    const { study } = useCurrentStudyConfigurationContext();

    const [analysisSets, setAnalysisSets] = React.useState<CoughAnalysisSet[]>([]);
    const [coughSettings, setCoughSettings] = React.useState<StudyCoughSetting>();
    const [coughEventTypes, setCoughEventTypes] = React.useState<StudyCoughEventType[]>([]);
    const [selectedSet, setSelectedSet] = React.useState<string>();
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();

    React.useEffect(() => {
        const getAnalysisSets = async () => {
            await client.client.getCoughAnalysisSets("id,typeName,statusName,firstComplete", `sessionId eq ${props.analysisSet.sessionId} and session/visit/subject/site/studyId eq ${study.id} and type eq 'CAS_ANALYSIS' and status eq 'ORS_ACCEPTED'`)
                .then((analysisSetsResult) => {
                    if (analysisSetsResult['@odata.count'] === 0) {
                        setError("There are no Analysis Sets present from which to create an Adjudication Analysis Set from");
                    }

                    setAnalysisSets(analysisSetsResult.value);
                });
        }
        const getCoughSettings = async () => {
            await legacyClient.getStudyCoughSettings(study.id)
                .then((coughSetting) => {

                    setCoughSettings(coughSetting);
                });
        }
        const getCoughEventTypes = async () => {

            await client.client.getStudyCoughEventTypes("id,period", `studyId eq ${study.id}`).then((coughEventTypes) => {

                setCoughEventTypes(coughEventTypes.value)
            });
        }

        getCoughEventTypes();
        getCoughSettings();
        getAnalysisSets();
    }, [study.id, legacyClient]);

    const createAdjudicationAnalysisSet = async (analysisSet: CoughAnalysisSet) => {

        setSubmitting(true);


        const selectFields = "id,eventTypeId,rawStart,rawEnd,processedStart,processedEnd,sensorProcessedStart,sensorProcessedEnd,analysisSetId";
        const analysisSetAEventsResult = await client.client.getCoughAnalysisSetEvents(selectFields,
            `analysisSetId eq ${analysisSet.id}`
        );

        const analysisSetBEventsResult = await client.client.getCoughAnalysisSetEvents(selectFields,
            `analysisSetId eq ${selectedSet}`
        );

        const adjudicationAnalysisSet = await client.client.postCoughAnalysisSet({
            sessionId: analysisSet.sessionId,
            type: CoughAnalysisSetTypeEnum.Adjudication,
            status: OverreadStatusIdEnum.NotStarted,
            adjudicationSourceSetId: analysisSet.id,
            adjudicationTargetSetId: selectedSet
           
        })

        const setA = analysisSetAEventsResult.value ?? [];
        const setB = analysisSetBEventsResult.value ?? [];
        const threshold = coughSettings.EventDuplicateThreshold / 1000;

        const isMatchPoint = (e1: any, e2: any) =>
            e1.eventTypeId === e2.eventTypeId &&
            Math.abs(e1.rawStart - e2.rawStart) <= threshold;

        const shouldPointMatch = (eventType: any) => !eventType?.period

        for (const evtA of setA) {
            const eventType = coughEventTypes.find(x => x.id === evtA.eventTypeId) as any;      

            const hasMatchInB = shouldPointMatch(eventType)
                ? setB.some(evtB => isMatchPoint(evtA, evtB))
                : false;



            const { analysisSetId: _ignoreA, ...eventA } = evtA;

            await client.client.postCoughAnalysisSetEvent({
                ...eventA,
                analysisSetId: adjudicationAnalysisSet.id,
                disputed: !hasMatchInB,
            });
        }


        for (const evtB of setB) {
            const eventType = coughEventTypes.find(x => x.id === evtB.eventTypeId) as any;

            const hasMatchInA = shouldPointMatch(eventType)
                ? setA.some(evtA => isMatchPoint(evtA, evtB))
                : false;

   
            if (hasMatchInA) {
                continue;
            }


            const { analysisSetId: _ignoreB, ...eventB } = evtB;

            await client.client.postCoughAnalysisSetEvent({
                ...eventB,
                analysisSetId: adjudicationAnalysisSet.id,
                disputed: true,
            });
        }

        props.onClose();       

    }

    const renderAnalysisSetsSelect = () => {
        if (analysisSets) {
            return (<select className="form-control" value={selectedSet} onChange={e => setSelectedSet(e.target.value)}>
                <option></option>
                {analysisSets.map(i => i.id !== props.analysisSet.id &&
                    <option key={i.id} value={i.id}>{i.typeName + " - " + FormatTime(i.firstComplete, DateTimeAccuracy.Minute, false) + " (" + i.statusName + ")"} </option>
                )}
            </select>);
        } else if (error) {
            return <Alert show={true} category={AlertCategoryEnum.Danger}>{error}</Alert>;
        } else if (submitting) {
            return <Loading />;
        } else {
            return null;
        }
    }

    return (<Modal show={true} closeModalHandler={() => props.onClose()} header="Create Adjudication Analysis Set">
        <div className='modal-body'>
            <p className="text-left">Combine with Analysis Set:</p>
            {renderAnalysisSetsSelect()}
        </div>
        <div className='modal-footer'>
            <button disabled={submitting || (selectedSet === undefined || selectedSet === null || selectedSet === "")} className='btn btn-primary' onClick={() => createAdjudicationAnalysisSet(props.analysisSet)}>Create Adjudication Analysis Set</button>
            <button className='btn btn-outline btn-primary' onClick={() => props.onClose()}>Close</button>
        </div>
    </Modal>);
}
